import { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
// 2026-07-10 — toast for the no-active-season warning (Phase 1.3).
import toast from 'react-hot-toast';
import {
  LayoutDashboard, Users, Calendar, MessageSquare,
  LogOut, Settings, Bell, BarChart2, Shield, User, Receipt,
  ChevronLeft, ChevronRight, Layers, Plus, DollarSign, Trophy, Radio, Globe,
} from 'lucide-react';
import { PRESS_BOX, pressBoxVisible, pressBoxNavItems } from '../constants/pressBox.js';
import { PWABanners } from '../components/shared/PWABanners.jsx';
import VersionTag from '../components/shared/VersionTag.jsx';
import { pathForTeamSwitch } from './teamSwitchNav.js';
import OnboardingFlow from '../components/OnboardingFlow.jsx';
import { iconForNotifType } from '../utils/notifIcons.js';
import { pickActiveTeam, saveLastTeamId } from '../utils/lastTeam.js';
import { usePWAContext } from '../context/PWAContext.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import Avatar from '../components/shared/Avatar.jsx';
import api from '../services/api.js';
import { chatSocket } from '../services/socket.js';
import { TEAM_SETTINGS_SUBSECTIONS } from './teamSettingsSubNav.js';
// v2.5.124 — Phase 11 League Module. When the user is on a
// /league/:leagueId/* path, the sidebar replaces team items with
// the league sub-nav. The hook centralizes the path detection,
// permission gating, and league info fetch so all four layouts
// stay in lockstep.
import { useLeagueModeNav } from '../hooks/useLeagueModeNav.js';
import { useCurrentSeason } from '../hooks/useCurrentSeason.js';

const SIDEBAR_KEY  = 'gameday-sidebar-collapsed';
const SIDEBAR_W    = 256;
const SIDEBAR_MINI = 64;

// ─────────────────────────────────────────────────────────────────
// Sidebar
// ─────────────────────────────────────────────────────────────────
function Sidebar({
  teams, activeTeam, setActiveTeam, user, onLogout,
  collapsed, onToggle,
  unreadCount, unreadNotifs, onNotifsClick, showNotifs,
  notifications, onMarkRead, onMarkAllRead,
  // v2.5.193 fix: the season pill below references these, but (unlike the
  // Mobile/Tablet/Capacitor drawers) they were never passed in. That left
  // `currentSeasonId` an undefined free variable -> ReferenceError that the
  // error boundary caught as "currentSeasonId is not defined", crashing
  // every desktop page once the per-season frontend went live.
  currentSeasonId, currentSeasonName,
}) {
  const location  = useLocation();
  const navigate  = useNavigate();
  const teamColor = activeTeam?.settings?.team_color || 'var(--ice)';

  // Apr 2026 — split nav by role.
  // Players see only Dashboard, Schedule, Chat, and a minimal "Team"
  // entry (so they can leave). Coach/manager-only entries (Players
  // management, Seasons, Stats, full Team Settings) are hidden so
  // players don't see clutter and gated 403s.
  const isCoachish = activeTeam && ['coach','manager'].includes(activeTeam.member_role);

  // 2026-10-18 — a Press Box team gets its own slim menu (games, roster,
  // site & settings) instead of the full team nav.
  const navItems = activeTeam?.team_type === 'press_box'
    ? pressBoxNavItems(activeTeam, { Radio, Users, Settings, BarChart3: BarChart2, Globe, Trophy })
    : activeTeam ? [
    { to: '/dashboard',                     icon: LayoutDashboard, label: 'Dashboard',     end: true },
    { to: `/team/${activeTeam.id}/games`,   icon: Calendar,        label: 'Schedule' },
    // Apr 30 2026 — Scoreboards. Visible to all roster members
    // (everyone reads scoreboards even if they can't edit). Hidden
    // when team setting scoreboard_enabled === false.
    ...(activeTeam.settings?.scoreboard_enabled !== false ? [
      { to: `/team/${activeTeam.id}/scoreboards`, icon: Trophy, label: 'Scoreboards' },
    ] : []),
    ...(isCoachish ? [
      { to: `/team/${activeTeam.id}/players`, icon: Users,           label: 'Players' },
    ] : []),
    { to: `/team/${activeTeam.id}/chat`,    icon: MessageSquare,   label: 'Chat',   badge: unreadCount },
    ...(isCoachish ? [
      { to: `/team/${activeTeam.id}/seasons`, icon: Layers,          label: 'Seasons' },
      { to: `/team/${activeTeam.id}/stats`,   icon: BarChart2,       label: 'Stats' },
      // Apr 2026 (Tom_Notes E2) — Finance tab. Coach-only — players
      // never see another player's payment status. The page itself
      // also re-checks coach role server-side via requireTeamRole.
      { to: `/team/${activeTeam.id}/finance`, icon: DollarSign,      label: 'Finance' },
      // Budget is OFF by default — only shown when a super admin has
      // enabled it for this team (teams.settings.budget_enabled).
      ...(activeTeam.settings?.budget_enabled === true ? [
        { to: `/team/${activeTeam.id}/budget`,  icon: DollarSign,      label: 'Budget' },
      ] : []),
      // Apr 2026 mobile-parity — Notification Log was previously
      // only on MobileLayout. Adding to Desktop so coaches working
      // from a laptop can also see the dispatch history of every
      // jab/reminder/announcement that's been sent. Backend 403s
      // players, so hidden for non-coaches.
      { to: `/team/${activeTeam.id}/notification-log`, icon: Bell, label: 'Notification Log' },
    ] : []),
    // May 4 2026 (v2.5.70) — coach feedback: "on the player side of
    // things, remove the 'Team' from players, they don't need to access
    // it." Settings sub-pages are all coach-gated server-side anyway,
    // and the only player-facing affordance there (Leave Team in the
    // Danger sub-page) has been moved to Profile to match. The whole
    // Team menu item now disappears for non-coaches.
    ...(isCoachish ? [
      { to: `/team/${activeTeam.id}/settings`, icon: Settings,
        label: 'Team Settings',
        // Apr 2026 v2 — sub-pages now render in a secondary panel that
        // slides in to the right of the main sidebar (see SettingsSubPanel
        // below). Pattern matches VS Code / Notion: clicking the parent
        // navigates AND opens the panel; navigating to any other top-level
        // item collapses it. The `children` array previously inlined here
        // is gone — the panel reads TEAM_SETTINGS_SUBSECTIONS directly.
      },
    ] : [
      // May 4 2026 (v2.5.70) — coach feedback: "for players, add in
      // stats that are specific to them. So add in a Stats section
      // (on the left hand menu), and report to them the stats that
      // only pertain to them." Player-scoped Stats route shows only
      // the current user's attendance, achievements, and ranking
      // among teammates — no leaderboard-of-everyone, no AI logs,
      // no per-player editorial controls. Distinct route from the
      // coach Stats so the data shape and page chrome can diverge
      // freely without conditionals.
      { to: `/team/${activeTeam.id}/my-stats`, icon: BarChart2, label: 'Stats' },
    ]),
    // v2.5.118 — Leagues nav is gated by the per-user 'leagues'
    // feature flag (see MobileLayout for rationale). Admins flip
    // it on per-user from /admin → user row → Feature flags.
  ] : [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', end: true },
  ];

  if (user?.featureFlags?.leagues) {
    navItems.push({ to: '/leagues', icon: Trophy, label: 'Leagues', accent: 'gold' });
  }
  // 2026-10-17 — Press Box (single-team stats), gated like Leagues: the
  // pressBox flag, or the super admin. The server re-checks every call.
  if (pressBoxVisible(user) && activeTeam?.team_type !== 'press_box') {
    navItems.push({ to: '/press-box', icon: Radio, label: PRESS_BOX.NAME, accent: 'gold' });
  }

  // v2.5.124 — League mode: when the URL is /league/:id/*, replace
  // the team-mode navItems with the league sub-nav (Dashboard,
  // Members, Settings, Venues, Divisions, Seasons, Players,
  // Schedule, Officials, Standings, Announcements). The team list
  // at the top of the sidebar also collapses to a single "Back to
  // teams" affordance, per the design decision in v2.5.124.
  const { inLeagueMode, leagueNavItems, league } = useLeagueModeNav();

  // 2026-10-22 (per Gilles) — an account whose teams are ALL Press Box sees
  // ONLY the Press Box menu, on every surface (web, desktop, mobile app).
  const pbOnlyTeams = (teams || []).filter(t => t.team_type === 'press_box');
  const pressBoxOnlyAccount = pbOnlyTeams.length > 0 && pbOnlyTeams.length === (teams || []).length;
  const navItemsPb = pressBoxOnlyAccount && activeTeam?.team_type !== 'press_box'
    ? pressBoxNavItems(pbOnlyTeams[0], { Radio, Users, Settings, BarChart3: BarChart2, Globe, Trophy })
    : navItems;
  const effectiveNavItems = inLeagueMode && leagueNavItems ? leagueNavItems : navItemsPb;

  const bottomItems = [
    {
      onClick: onNotifsClick,
      icon: Bell,
      label: 'Alerts',
      badge: unreadNotifs,
      active: showNotifs,
    },
    { to: '/profile', icon: User, label: 'Profile' },
    // Apr 2026 — Activity only shows for users who've actually
    // transacted (payment or credit row exists). Flag comes from
    // /auth/me and is refreshed whenever the AuthContext refetches.
    // Users who've never paid a spare/season fee never see this
    // link, keeping the nav clean for the common case.
    ...(user?.hasPaymentActivity ? [{ to: '/activity', icon: Receipt, label: 'Activity' }] : []),
    ...(user?.role === 'admin' ? [{ to: '/admin', icon: Shield, label: 'Admin', fire: true }] : []),
  ];

  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const isActive = item.to
      ? (item.end ? location.pathname === item.to : location.pathname.startsWith(item.to))
      : item.active;

    // v2.5.126 — accent flag lets a nav item stand out with a
    // theme-aware color. Currently used for Leagues ('gold') so
    // users recognize it as a different mode. fire/penalty/etc.
    // could be added the same way later. Resolved here so the
    // styling logic below stays clean.
    const accent = item.accent;
    const accentColor =
      accent === 'gold' ? 'var(--gold)' :
      item.fire ? 'var(--fire)' : 'var(--ice)';
    const accentBg =
      accent === 'gold' ? 'var(--gold-a15)' :
      item.fire ? 'var(--fire-a8)' : 'var(--ice-a8)';
    const accentIdleColor =
      accent === 'gold' ? 'var(--gold-a50)' :
      item.fire ? 'rgba(255,96,64,0.55)' : 'var(--text-2)';

    const inner = (
      <span style={{
        display: 'flex', alignItems: 'center',
        gap: collapsed ? 0 : 10,
        padding: collapsed ? '10px 0' : '9px 12px',
        borderRadius: 10,
        width: '100%',
        justifyContent: collapsed ? 'center' : 'flex-start',
        background: isActive ? accentBg : 'transparent',
        color: isActive ? accentColor : accentIdleColor,
        transition: 'all 0.18s',
        position: 'relative',
        minHeight: 40,
        cursor: 'pointer',
      }}>
        {/* Active bar */}
        {isActive && (
          <span style={{
            position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
            width: 3, height: 18, borderRadius: 2,
            background: accentColor,
            boxShadow: `0 0 8px ${accentColor}`,
          }} />
        )}
        {/* Icon + badge */}
        <span style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
          <Icon size={16} strokeWidth={isActive ? 2.2 : 1.8} />
          {(item.badge || 0) > 0 && (
            <span style={{
              position: 'absolute', top: -5, right: -6,
              minWidth: 14, height: 14, padding: '0 3px', borderRadius: 7,
              background: 'var(--penalty)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 8, fontWeight: 800, lineHeight: 1,
            }}>
              {item.badge > 9 ? '9+' : item.badge}
            </span>
          )}
        </span>
        {/* Label */}
        {!collapsed && (
          <span style={{
            flex: 1,
            fontFamily: 'var(--font-body)',
            fontSize: 13.5, fontWeight: isActive ? 600 : 450,
            overflow: 'hidden', whiteSpace: 'nowrap',
          }}>
            {item.label}
          </span>
        )}
      </span>
    );

    const wrapStyle = {
      display: 'block', textDecoration: 'none',
      border: 'none', background: 'transparent',
      width: '100%', textAlign: 'left',
      padding: collapsed ? '1px 12px' : '1px 8px',
    };

    if (item.onClick) {
      return (
        <button title={collapsed ? item.label : undefined}
          onClick={item.onClick} style={{ ...wrapStyle, cursor: 'pointer' }}>
          {inner}
        </button>
      );
    }
    return (
      <NavLink title={collapsed ? item.label : undefined}
        to={item.to} end={item.end} style={wrapStyle}>
        {inner}
      </NavLink>
    );
  };

  return (
    <aside style={{
      width: collapsed ? SIDEBAR_MINI : SIDEBAR_W,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
      position: 'relative',
    }}>

      {/* Team color bar */}
      <div style={{
        height: 3, flexShrink: 0,
        background: `linear-gradient(90deg, ${teamColor} 0%, transparent 70%)`,
        transition: 'background 0.5s',
      }} />

      {/* Logo + collapse button */}
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: collapsed ? '14px 0' : '14px 14px 10px',
        justifyContent: collapsed ? 'center' : 'space-between',
        flexShrink: 0,
      }}>
        {!collapsed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
            <div style={{
              width: 34, height: 34, borderRadius: 10, flexShrink: 0,
              background: `linear-gradient(135deg, ${teamColor}28, ${teamColor}0a)`,
              border: `1px solid ${teamColor}35`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
            }}>🏟</div>
            <div>
              <div style={{
                fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 18,
                letterSpacing: '0.07em', textTransform: 'uppercase', lineHeight: 1,
                color: 'var(--text)',
              }}>Game Day</div>
              <div style={{
                fontSize: 8, letterSpacing: '0.2em', fontFamily: 'var(--font-display)',
                color: teamColor, textTransform: 'uppercase', marginTop: 2,
              }}>TEAM MANAGER</div>
            </div>
          </div>
        )}

        {/* Collapse toggle */}
        <button
          onClick={onToggle}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          style={{
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            color: 'var(--text-2)', cursor: 'pointer',
            transition: 'all 0.15s',
          }}>
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {/* League-mode header — v2.5.124. Replaces the team list with
          a short "LEAGUE / <name>" caption + a back-to-teams link.
          When collapsed, only the trophy icon + back chevron show. */}
      {inLeagueMode && (
        <div style={{ padding: '0 8px', marginBottom: 8, flexShrink: 0 }}>
          {!collapsed && (
            <>
              <p style={{
                fontFamily: 'var(--font-display)', fontSize: 9, letterSpacing: '0.18em',
                textTransform: 'uppercase', color: 'var(--text-2)',
                padding: '0 8px', marginBottom: 4,
              }}>League Mode</p>
              <div style={{
                padding: '8px 10px 8px 12px', borderRadius: 10, marginBottom: 4,
                background: 'rgba(178, 132, 255, 0.10)',
                border: '1px solid rgba(178, 132, 255, 0.30)',
                display: 'flex', alignItems: 'center', gap: 8, minWidth: 0,
              }}>
                <Trophy size={14} style={{ color: '#b284ff', flexShrink: 0 }} />
                <span style={{
                  fontSize: 13, fontWeight: 700, color: 'var(--text)',
                  overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                  flex: 1, minWidth: 0,
                }}>
                  {league?.name || 'Loading…'}
                </span>
              </div>
            </>
          )}
          <button
            onClick={() => navigate('/dashboard')}
            title={collapsed ? 'Back to teams' : undefined}
            style={{
              width: '100%', textAlign: 'left', borderRadius: 10, marginTop: 4,
              padding: collapsed ? '8px 0' : '8px 10px 8px 12px',
              background: 'transparent',
              border: '1px dashed var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 8, cursor: 'pointer', color: 'var(--text-2)',
              fontFamily: 'var(--font-body)', fontSize: 12,
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--ice)'; e.currentTarget.style.color = 'var(--ice)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; }}
          >
            <ChevronLeft size={14} style={{ flexShrink: 0 }} />
            {!collapsed && <span style={{ fontWeight: 600 }}>Back to teams</span>}
          </button>
        </div>
      )}

      {/* Team selector — hidden in league mode (v2.5.124). The
          league mode pill in the header is the user's way back
          to team mode (clicking it returns to /dashboard). */}
      {!inLeagueMode && teams.length > 0 && (
        <div style={{ padding: collapsed ? '0 8px' : '0 8px', marginBottom: 8, flexShrink: 0 }}>
          {!collapsed && (
            <p style={{
              fontFamily: 'var(--font-display)', fontSize: 9, letterSpacing: '0.18em',
              textTransform: 'uppercase', color: 'var(--text-2)',
              padding: '0 8px', marginBottom: 4,
            }}>Your Teams</p>
          )}
          {teams.map(team => {
            const isActive = activeTeam?.id === team.id;
            const c = team.settings?.team_color || 'var(--ice)';
            return (
              <button
                key={team.id}
                title={collapsed ? team.name : undefined}
                onClick={() => {
                  // Apr 30 2026 — URL rewrite so useParams().teamId pages
                  // follow the switch. 2026-07-10 (coach report: rotating
                  // between teams still sometimes left pages showing the
                  // previous team) — switching is now a FULL PAGE LOAD:
                  // every component remounts against the new team, so no
                  // stale context/effect/query can survive. Rare action,
                  // PWA-cached shell → fast. The chosen id is persisted
                  // FIRST so boot-time pickActiveTeam restores the right
                  // team even when the PATCH /auth/last-team is cancelled
                  // by this very navigation (or rate-limited).
                  if (activeTeam?.id !== team.id) {
                    try { localStorage.setItem('gd:lastTeamId', team.id); } catch {}
                    const target = pathForTeamSwitch(location.pathname, activeTeam?.id, team.id, team);
                    window.location.assign(target || '/dashboard');
                  }
                }}
                style={{
                  width: '100%', textAlign: 'left', borderRadius: 10, marginBottom: 2,
                  padding: collapsed ? '8px 0' : '8px 10px 8px 12px',
                  background: isActive ? `${c}10` : 'transparent',
                  border: `1px solid ${isActive ? `${c}28` : 'transparent'}`,
                  display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
                  gap: 8, cursor: 'pointer', transition: 'all 0.18s',
                }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: c, opacity: isActive ? 1 : 0.35,
                  boxShadow: isActive ? `0 0 8px ${c}` : 'none',
                }} />
                {!collapsed && (
                  <span style={{
                    fontSize: 13, fontWeight: 600, color: isActive ? c : 'var(--text-2)',
                    overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                    transition: 'color 0.2s',
                  }}>{team.name}</span>
                )}
              </button>
            );
          })}
          {/* Apr 2026 — "+ New Team" at the bottom of the team list.
              Always visible so any player (including those on other
              teams as regular players) can spin up their own team and
              become its coach. Navigates to /dashboard?new=1 which
              pops the create-team wizard. */}
          <button
            onClick={() => navigate('/dashboard?new=1')}
            title={collapsed ? 'New Team' : undefined}
            style={{
              width: '100%', textAlign: 'left', borderRadius: 10, marginTop: 4,
              padding: collapsed ? '8px 0' : '8px 10px 8px 12px',
              background: 'transparent',
              border: '1px dashed var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 8, cursor: 'pointer', color: 'var(--text-2)',
              fontFamily: 'var(--font-body)', fontSize: 12,
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--ice)'; e.currentTarget.style.color = 'var(--ice)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; }}
          >
            <Plus size={14} style={{ flexShrink: 0 }} />
            {!collapsed && <span style={{ fontWeight: 600 }}>New Team</span>}
          </button>
        </div>
      )}

      {/* Divider */}
      <div className="ice-divider" style={{ margin: collapsed ? '4px 12px 8px' : '0 20px 8px', flexShrink: 0 }} />

      {/* Main nav */}
      {/* 2026-07-10 (coach request) — the SEASON selector card that sat
          here was removed as a duplicate of the "Seasons" nav item below.
          Season scope is still fully functional: useCurrentSeason keeps
          feeding the Outlet context, and SeasonsPage's "work in this
          season" action (goToSeason) remains the way to switch scope. */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '0 0 8px' }}>
          {effectiveNavItems.map((item, i) => <NavItem key={i} item={item} />)}
      </nav>

      {/* Divider */}
      <div className="ice-divider" style={{ margin: collapsed ? '4px 12px 8px' : '4px 20px 8px', flexShrink: 0 }} />

      {/* Bottom items: Alerts, Profile, Admin */}
      <div style={{ flexShrink: 0, paddingBottom: 8 }}>
        {bottomItems.map((item, i) => <NavItem key={i} item={item} />)}

        {/* User / sign out */}
        <div style={{
          margin: collapsed ? '8px 8px 0' : '8px 8px 0',
          padding: '8px',
          borderRadius: 10,
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? 0 : 8,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}>
          {/* Avatar */}
          <Avatar avatarUrl={user?.avatarUrl || null} name={user?.name} size={28} />
          {!collapsed && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', lineHeight: 1.2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                  {user?.name}
                </div>
                <div style={{ fontSize: 9, color: 'var(--goal)', fontFamily: 'var(--font-display)', letterSpacing: '0.08em' }}>
                  ● ONLINE
                </div>
              </div>
              <button onClick={onLogout} title="Sign out"
                style={{
                  flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 26, height: 26, borderRadius: 7,
                  background: 'transparent', border: '1px solid var(--border)',
                  color: 'var(--penalty-a60)', cursor: 'pointer', transition: 'all 0.15s',
                }}>
                <LogOut size={12} />
              </button>
            </>
          )}
        </div>

        {/* Apr 2026 — version badge.
            Shows the current app version (from VERSION file via vite
            define) so support / debug conversations have a concrete
            reference point. Tiny + low-contrast so it's noise unless
            you're looking for it. Hidden when the sidebar is
            collapsed (no horizontal room) and on the mobile/tablet
            layouts where bottom-bar real estate is more precious. */}
        {/* Apr 30 2026 — extracted to shared VersionTag component
            so all 4 layouts use the same drift-aware display.
            Click to expand for backend version + build SHAs. */}
        {!collapsed && <VersionTag />}
      </div>

      {/* Notification dropdown (shown inline below Alerts when expanded) */}
      {showNotifs && !collapsed && (
        <NotificationPanel
          notifications={notifications}
          onMarkRead={onMarkRead}
          onMarkAllRead={onMarkAllRead}
          style={{
            position: 'absolute', bottom: 180, left: 8, right: 8,
            zIndex: 200, maxHeight: '50vh',
          }}
        />
      )}
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────
// Notification panel
// ─────────────────────────────────────────────────────────────────
function NotificationPanel({ notifications, onMarkRead, onMarkAllRead, style }) {
  // Apr 2026 — icons centralised in utils/notifIcons.js. Was a hand-
  // maintained map here that drifted out of sync with the mobile,
  // tablet, and capacitor copies whenever a new notification type
  // shipped. Adding a new type now takes one file edit, not four.

  return (
    <div className="glass shadow-2xl" style={{
      display: 'flex', flexDirection: 'column', borderRadius: 14,
      overflow: 'hidden', ...style,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0,
      }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>Notifications</span>
        <button onClick={onMarkAllRead}
          style={{ fontSize: 11, color: 'var(--ice)', background: 'none', border: 'none', cursor: 'pointer' }}>
          Mark all read
        </button>
      </div>
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {notifications.length === 0 ? (
          <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--text-2)', fontSize: 13 }}>
            All caught up!
          </div>
        ) : notifications.slice(0, 25).map(n => (
          <button key={n.id} onClick={() => onMarkRead(n.id)}
            style={{
              width: '100%', textAlign: 'left',
              padding: '10px 14px',
              borderBottom: '1px solid var(--border)',
              background: n.read_at ? 'transparent' : 'var(--ice-a3)',
              display: 'flex', alignItems: 'flex-start', gap: 10,
              border: 'none', cursor: 'pointer',
            }}>
            <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{iconForNotifType(n.type)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{n.title}</span>
                {!n.read_at && <div style={{ width: 6, height: 6, borderRadius: '50%', flexShrink: 0, background: 'var(--ice)' }} />}
              </div>
              {n.body && <p style={{ fontSize: 11, color: 'var(--text-2)', margin: '2px 0 0' }}>{n.body}</p>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SettingsSubPanel (Apr 2026 v2)
//
// Secondary navigation column that slides in to the right of the main
// sidebar when the user is on any /team/:id/settings/* route. Shows
// all 12 settings sub-pages stacked vertically with no scroll, so the
// coach can see every option at a glance.
//
// Auto-show/hide is purely a function of the URL — no local toggle
// state. Click "Team Settings" in the main sidebar -> URL changes to
// a /settings/* path -> panel renders. Click any other top-level item
// -> URL changes off /settings/* -> panel disappears. This means
// navigation between sub-pages keeps the panel open, but navigation
// to a sibling top-level item (Schedule, Players, etc.) collapses it
// without any extra logic.
//
// The panel is rendered ONLY for coaches/managers (the only role with
// 12 sub-pages); players see only the simple "Team" link in the main
// sidebar without the panel.
//
// Width: 200px. Wider than the main sidebar's children-indent style
// would have been, narrower than the main sidebar itself. Combined
// width with collapsed-mini main sidebar = 64+200 = 264px which is
// less than two regular sidebars (224*2=448) so even on narrower
// laptop screens this fits comfortably.
// ─────────────────────────────────────────────────────────────────
function SettingsSubPanel({ teams }) {
  const location = useLocation();
  // Apr 2026 v2.1 — extract teamId from the URL itself rather than
  // relying on the layout's activeTeam state. They can drift: if a
  // coach has multiple teams and bookmarks /team/A/settings/pricing,
  // the layout's activeTeam might still be team B from a prior
  // session. The panel's links MUST point to the same team the
  // page is actually showing, not the activeTeam state. We also
  // re-check the user's role on the URL's team (not activeTeam's
  // role) so a player on team A who happens to be coach on team B
  // isn't shown a settings panel they can't actually use.
  const match = location.pathname.match(/^\/team\/([^/]+)\/settings(?:\/|$)/);
  const teamId = match ? match[1] : null;
  const teamForUrl = teamId ? (teams || []).find(t => t.id === teamId) : null;
  const isCoachish = teamForUrl && ['coach', 'manager'].includes(teamForUrl.member_role);

  if (!isCoachish || !teamId) return null;

  return (
    <aside
      aria-label="Team settings sections"
      style={{
        width: 200,
        flexShrink: 0,
        height: '100dvh',
        position: 'sticky',
        top: 0,
        background: 'var(--surface)',
        borderRight: '1px solid var(--border)',
        zIndex: 49,
        overflow: 'auto',
        // Subtle slide-in feel when the panel first appears. No exit
        // animation since the panel is unmounted, not toggled, when the
        // user navigates away — that's the cleanest pattern when
        // visibility is purely route-derived.
        animation: 'settingsPanelSlideIn 0.22s cubic-bezier(0.4,0,0.2,1)',
      }}>
      {/* CSS keyframes registered once via a style tag (scoped enough
          since the animation name is unique). Avoids needing to add
          to global CSS for a single component's effect. */}
      <style>{`
        @keyframes settingsPanelSlideIn {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      <div style={{ padding: '20px 16px 12px' }}>
        <p style={{
          fontFamily: 'var(--font-display)',
          fontSize: 10, fontWeight: 700,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'var(--text-3)', margin: 0,
        }}>
          Team Settings
        </p>
      </div>

      <nav style={{ padding: '0 8px 16px' }}>
        {TEAM_SETTINGS_SUBSECTIONS.map(s => {
          const to = `/team/${teamId}/settings/${s.id}`;
          return (
            <NavLink
              key={s.id}
              to={to}
              end
              style={({ isActive }) => ({
                display: 'block',
                padding: '8px 12px',
                marginBottom: 1,
                borderRadius: 8,
                textDecoration: 'none',
                fontFamily: 'var(--font-body)',
                fontSize: 13.5,
                fontWeight: isActive ? 600 : 450,
                color: isActive ? 'var(--ice)' : 'var(--text-2)',
                background: isActive ? 'var(--ice-a8)' : 'transparent',
                position: 'relative',
                transition: 'all 0.15s',
              })}>
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span style={{
                      position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
                      width: 3, height: 16, borderRadius: 2,
                      background: 'var(--ice)',
                      boxShadow: '0 0 6px var(--ice)',
                    }} />
                  )}
                  {s.label}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────
// DesktopLayout
// ─────────────────────────────────────────────────────────────────
export default function DesktopLayout() {
  const { user, logout } = useAuth();
  const { offline } = usePWAContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Sidebar collapsed state — persisted to localStorage
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === 'true'; } catch { return false; }
  });

  const [teams, setTeams]               = useState([]);
  // May 1 2026 — true after the first /teams fetch resolves (success
  // OR failure). Lets consumers (DashboardPage empty state) tell
  // "we haven't tried to load yet" apart from "we loaded and the
  // user really has no teams." Without this, refreshing the
  // dashboard for a user who DOES have teams briefly flashed the
  // "You're not on a schedule yet" empty state during the gap
  // between mount and the /teams fetch resolving.
  const [teamsLoaded, setTeamsLoaded]   = useState(false);
  const [activeTeam, setActiveTeam]     = useState(null);
  const { currentSeasonId, currentSeasonName, setCurrentSeason, seasonLoaded } = useCurrentSeason(activeTeam?.id || null);
  const [unreadCount, setUnreadCount]   = useState(0);
  const [showNotifs, setShowNotifs]     = useState(false);
  const [notifications, setNotifications] = useState([]);

  const toggleSidebar = () => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem(SIDEBAR_KEY, String(next)); } catch {}
      return next;
    });
  };

  // Load teams. On first call (`prev` is null) the helper picks
  // the user's saved last-active team from the server (passed via
  // the user object) or falls back to teams[0]. On subsequent
  // refreshes we keep the current selection if it's still in the
  // list, falling back to saved id only if not.
  //
  // We accept `lastTeamId` as a parameter (rather than reading from
  // `user` via closure) because the loadTeams callback is memoized
  // with empty deps — a closure read would capture user=null at
  // mount time and never see the actual logged-in user. Passing it
  // as an arg keeps the callback identity stable while still using
  // fresh data each call.
  // 2026-07-10 (Phase 1.3, coach request) — warning toast when the working
  // team has no active season. Migration 103 backfills every existing team,
  // so this firing means a genuinely broken state worth reporting. Dedupe
  // per team per mount so route changes don't re-nag.
  const seasonWarnedRef = useRef(new Set());
  useEffect(() => {
    if (!activeTeam?.id) return;
    if (activeTeam.active_season_id) return;
    if (seasonWarnedRef.current.has(activeTeam.id)) return;
    seasonWarnedRef.current.add(activeTeam.id);
    toast(`⚠️ ${activeTeam.name} has no active season — create one under Seasons so games and stats are scoped correctly.`,
      { icon: '⚠️', duration: 8000 });
  }, [activeTeam]);

  const loadTeams = useCallback(async (lastTeamId = null, _attempt = 0) => {
    try {
      const { data } = await api.get('/teams');
      setTeams(data);
      setActiveTeam(prev => pickActiveTeam(data, prev, lastTeamId));
      setTeamsLoaded(true);
    } catch (err) {
      // v2.5.184 — a transient failure here (notably a 429 from rapid
      // refreshes, or a 5xx/network blip) must NOT collapse the UI to
      // the "You're not on a schedule yet" empty state: the user is
      // still logged in and DOES have teams, we just couldn't fetch
      // them this instant. Previously the catch was a no-op and the
      // finally set teamsLoaded=true, so teams=[] + teamsLoaded=true
      // rendered the empty state — exactly the misleading screen the
      // coach reported. Now: retry once after a short backoff, and only
      // give up (mark loaded so the user isn't stuck on a skeleton) if
      // the retry also fails. 401 is left to the api.js interceptor
      // (refresh/redirect) — we don't retry those here.
      const status = err?.response?.status;
      if (status === 401) { setTeamsLoaded(true); return; }
      if (_attempt < 1) {
        setTimeout(() => { loadTeams(lastTeamId, _attempt + 1); }, 1500);
        return; // keep teamsLoaded as-is (false on first load → skeleton stays)
      }
      // Retry exhausted. Mark loaded so the app isn't frozen; if we
      // already had teams from a prior successful load, they remain.
      setTeamsLoaded(true);
    }
  }, []);

  // Persist the active team id to the SERVER whenever it changes.
  // (Apr 2026 — moved from localStorage to user.last_active_team_id
  // so the preference syncs across devices.) Fire-and-forget; failure
  // doesn't block the UI. null clears the preference (e.g. user
  // hasn't selected a team yet because they haven't joined any).
  useEffect(() => {
    if (activeTeam?.id) saveLastTeamId(activeTeam.id);
  }, [activeTeam?.id]);

  // Apply team color
  useEffect(() => {
    const color = activeTeam?.settings?.team_color;
    document.documentElement.style.setProperty('--ice', color || '#00d4ff');
  }, [activeTeam?.id, activeTeam?.settings?.team_color]);

  useEffect(() => {
    // Apr 2026 — wait for user to load before loading teams, so we
    // can pass the saved last-active-team id from the user object.
    // Without this gate, loadTeams runs on mount with user=null and
    // falls back to teams[0], ignoring the saved preference. Once
    // user loads, this effect re-fires with the real user.id and
    // passes the saved id to pickActiveTeam.
    if (user?.id) {
      loadTeams(user.lastActiveTeamId || null);
    }
    const token = localStorage.getItem('token');
    if (token) chatSocket.connect(token);
    return () => chatSocket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // 2026-07-21 (multi-team email-link landing audit) — correct activeTeam
  // when it drifts from the team the CURRENT URL is actually pointing at.
  // Root cause: activeTeam is chosen once from localStorage 'gd:lastTeamId'
  // / the server's saved preference (pickActiveTeam, in loadTeams above) —
  // never re-derived from the URL itself. A multi-team user who opens a
  // team-scoped email link (chat message, roster announcement, drinks
  // alert, etc.) for a team OTHER than their last-active one lands on the
  // right team-scoped PAGE (every /team/:id/* page reads :teamId from the
  // URL directly, not from activeTeam), but activeTeam itself stays wrong.
  // That's more than cosmetic: this layout feeds useCurrentSeason(activeTeam.id)
  // above, and pages like Chat/Schedule/Players default their season
  // filter to that (wrong) value when landing directly — silently
  // filtering the RIGHT team's data by the WRONG team's season id and
  // rendering empty. Same fix family as the RSVP magic-login gd:lastTeamId
  // pin (RsvpResultPage) and the GamePage teamId-resolution pin — applied
  // here at the layout level since every /team/:teamId/* route shares this
  // same activeTeam/outlet-context mechanism. Also fixes SPA-internal
  // navigations (push notifications, deep links) where the layout never
  // remounts, so a plain localStorage pin alone wouldn't be read again.
  useEffect(() => {
    if (!teamsLoaded || teams.length === 0) return;
    const match = location.pathname.match(/^\/team\/([^/]+)/);
    const urlTeamId = match ? match[1] : null;
    if (!urlTeamId || urlTeamId === activeTeam?.id) return;
    const urlTeam = teams.find(t => t.id === urlTeamId);
    if (!urlTeam) return; // not a member (or a bad id) — leave activeTeam as-is
    setActiveTeam(urlTeam);
    try { localStorage.setItem('gd:lastTeamId', urlTeamId); } catch { /* storage unavailable — non-fatal */ }
  }, [location.pathname, teams, teamsLoaded, activeTeam?.id]);

  // Unread chat count
  useEffect(() => {
    const unsub = chatSocket.on('NEW_MESSAGE', () => {
      if (!location.pathname.includes('/chat')) setUnreadCount(c => c + 1);
    });
    return unsub;
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.includes('/chat')) setUnreadCount(0);
  }, [location.pathname]);

  // Close notifs on navigate
  useEffect(() => { setShowNotifs(false); }, [location.pathname]);

  // Notifications
  const loadNotifications = useCallback(async () => {
    try { const { data } = await api.get('/notifications'); setNotifications(data); } catch {}
  }, []);

  useEffect(() => { loadNotifications(); }, []);
  useEffect(() => {
    const interval = setInterval(loadNotifications, 30_000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  useEffect(() => {
    const unsub = chatSocket.on('NEW_NOTIFICATION', (notif) => {
      if (notif) setNotifications(prev => [{ ...notif, read_at: null }, ...prev.filter(n => n.id !== notif.id)]);
      else loadNotifications();
    });
    return unsub;
  }, [loadNotifications]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const unreadNotifs = notifications.filter(n => !n.read_at).length;

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(p => p.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
      const notif = notifications.find(n => n.id === id);
      if (notif?.game_id) navigate(`/game/${notif.game_id}`);
      else if (notif?.team_id) navigate(`/team/${notif.team_id}/chat`);
      setShowNotifs(false);
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(p => p.map(n => ({ ...n, read_at: new Date().toISOString() })));
    } catch {}
    setShowNotifs(false);
  };

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', background: 'var(--bg)' }}>

      {/* ── Sidebar — always visible, collapsible ── */}
      <div style={{
        width: collapsed ? SIDEBAR_MINI : SIDEBAR_W,
        flexShrink: 0,
        height: '100dvh',
        position: 'sticky',
        top: 0,
        background: 'var(--sidebar-bg)',
        borderRight: '1px solid var(--sidebar-border)',
        backdropFilter: 'blur(20px)',
        transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
        overflow: 'hidden',
        zIndex: 50,
      }}>
        <Sidebar
          teams={teams}
          activeTeam={activeTeam}
          setActiveTeam={setActiveTeam}
          user={user}
          onLogout={handleLogout}
          collapsed={collapsed}
          onToggle={toggleSidebar}
          unreadCount={unreadCount}
          unreadNotifs={unreadNotifs}
          onNotifsClick={() => setShowNotifs(p => !p)}
          showNotifs={showNotifs}
          notifications={notifications}
          onMarkRead={markRead}
          onMarkAllRead={markAllRead}
          currentSeasonId={currentSeasonId}
          currentSeasonName={currentSeasonName}
        />
      </div>

      {/* ── Settings sub-panel ──
          Renders ONLY when the URL is on a /team/:id/settings/* route
          AND the user is coach/manager on that team. Sits between the
          main sidebar and the main content area as a 200px column.
          Auto-hides when the URL leaves /settings/*. teamId AND role
          are derived from the URL inside the panel itself, not passed
          from here, so the panel always matches the page being shown
          even with multi-team users / deep links / bookmarks. */}
      <SettingsSubPanel teams={teams} />

      {/* ── Main content ── */}
      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>

        {/* Slim top bar — just offline warning and page context */}
        {offline.isOffline && (
          <div style={{
            background: 'rgba(255,59,59,0.95)', backdropFilter: 'blur(10px)',
            padding: '6px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fff',
          }}>
            ● Offline — cached data shown
          </div>
        )}

        <div style={{ flex: 1, padding: '24px', maxWidth: 1200, width: '100%', margin: '0 auto' }}>
          <Outlet context={{ teams, teamsLoaded, activeTeam, setActiveTeam, loadTeams, currentSeasonId, currentSeasonName, setCurrentSeason, seasonLoaded }} />
        </div>
      </main>

      <OnboardingFlow teams={teams} activeTeam={activeTeam} />
      <PWABanners />
    </div>
  );
}
